import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { Upload as UploadIcon, MapPin, Calendar, User } from 'lucide-react';
import { footageService } from '../services/FootageService';

export const Upload: React.FC = () => {
  const { user } = useAuth();
  const { company } = useCompany();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [uploadErrors, setUploadErrors] = useState<{ [key: string]: string }>({});
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [metadata, setMetadata] = useState({
    location: '',
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    notes: '',
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setSelectedFiles(files);
      setMessage(null);
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one file' });
      return;
    }

    if (!metadata.location) {
      setMessage({ type: 'error', text: 'Please enter a location' });
      return;
    }

    if (!user || !company) {
      setMessage({ type: 'error', text: 'User or company information not available' });
      return;
    }

    setUploading(true);
    setMessage(null);
    setUploadErrors({});

    try {
      console.log('=== Upload Started ===');
      console.log('Company ID:', company.id);
      console.log('User ID:', user.id);
      console.log('User Name:', user.name);
      console.log('Files to upload:', selectedFiles.length);

      const incidentDate = new Date(metadata.date);
      const [hours, minutes] = metadata.time.split(':');
      incidentDate.setHours(parseInt(hours), parseInt(minutes));

      let successCount = 0;
      let failCount = 0;

      // Upload each file
      for (const file of selectedFiles) {
        const fileName = file.name;
        console.log(`Starting upload for: ${fileName}`);
        setUploadProgress((prev) => ({ ...prev, [fileName]: 0 }));

        try {
          const result = await footageService.uploadFootage(
            company.id,
            user.id,
            user.name,
            {
              file,
              incidentDate,
              location: metadata.location,
              tags: metadata.notes ? [metadata.notes] : [],
            },
            {
              pattern: company.settings?.namingConvention || '{name}_{date}_{vid}',
              dateFormat: 'YYYYMMDD',
              separator: '_',
              videoNumberPadding: 3,
            },
            company.settings?.folderStructure || 'user',
            (progress) => {
              console.log(`Upload progress for ${fileName}: ${progress}%`);
              setUploadProgress((prev) => ({ ...prev, [fileName]: progress }));
            }
          );
          console.log(`Upload completed for: ${fileName}`, result);
          setUploadProgress((prev) => ({ ...prev, [fileName]: 100 }));
          successCount++;
        } catch (fileError) {
          console.error(`Error uploading ${fileName}:`, fileError);
          const errorMessage = fileError instanceof Error ? fileError.message : 'Unknown error';
          setUploadErrors((prev) => ({ ...prev, [fileName]: errorMessage }));
          failCount++;
        }
      }

      console.log(`=== Upload Summary: ${successCount} succeeded, ${failCount} failed ===`);

      if (successCount > 0 && failCount === 0) {
        setMessage({
          type: 'success',
          text: `Successfully uploaded ${successCount} file(s)! You can now view them in the Footage Library.`,
        });
        setSelectedFiles([]);
        setUploadProgress({});
        setMetadata({
          location: '',
          date: new Date().toISOString().split('T')[0],
          time: new Date().toTimeString().slice(0, 5),
          notes: '',
        });
      } else if (successCount > 0 && failCount > 0) {
        setMessage({
          type: 'error',
          text: `Partially completed: ${successCount} file(s) uploaded successfully, ${failCount} failed. Check individual file errors below.`,
        });
      } else {
        setMessage({
          type: 'error',
          text: `All uploads failed. Please check your internet connection and try again.`,
        });
      }
    } catch (error) {
      console.error('=== Upload Error ===', error);
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
      }
      setMessage({ type: 'error', text: `Failed to upload files: ${error instanceof Error ? error.message : 'Unknown error'}` });
    } finally {
      setUploading(false);
    }
  };

  if (user?.role !== 'admin' && user?.role !== 'supervisor' && user?.role !== 'rbt') {
    return (
      <div className="container" style={{ paddingTop: 'var(--spacing-8)' }}>
        <div className="alert alert-error">You do not have permission to upload footage.</div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: 'var(--spacing-8)', paddingBottom: 'var(--spacing-8)' }}>
        <div style={{ marginBottom: 'var(--spacing-8)' }}>
          <h1 style={{ marginBottom: 'var(--spacing-2)' }}>Upload Footage</h1>
          <p style={{ color: 'var(--color-gray-600)' }}>
            Upload body camera footage with location and incident details
          </p>
        </div>

        {message && (
          <div className={`alert alert-${message.type}`} style={{ marginBottom: 'var(--spacing-6)' }}>
            {message.text}
          </div>
        )}

        <div className="grid grid-cols-2" style={{ gap: 'var(--spacing-6)' }}>
          {/* File Upload Section */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">Select Files</h2>
            </div>

            <div
              style={{
                border: '2px dashed var(--color-gray-300)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--spacing-8)',
                textAlign: 'center',
                backgroundColor: 'var(--color-gray-50)',
              }}
            >
              <UploadIcon size={48} style={{ margin: '0 auto var(--spacing-4)', color: 'var(--color-gray-400)' }} />
              <h3 style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--spacing-2)' }}>
                Drop files here or click to upload
              </h3>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-4)' }}>
                Support for video files (MP4, AVI, MOV)
              </p>
              <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                Select Files
                <input
                  type="file"
                  accept="video/*"
                  multiple
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </label>
            </div>

            {selectedFiles.length > 0 && (
              <div style={{ marginTop: 'var(--spacing-6)' }}>
                <h4 style={{ fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-3)' }}>
                  Selected Files ({selectedFiles.length})
                </h4>
                {selectedFiles.map((file, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-3)',
                      padding: 'var(--spacing-3)',
                      backgroundColor: 'var(--color-gray-50)',
                      borderRadius: 'var(--radius-md)',
                      marginBottom: 'var(--spacing-2)',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>{file.name}</p>
                      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)' }}>
                        {formatFileSize(file.size)}
                      </p>
                      {uploadProgress[file.name] !== undefined && (
                        <div
                          style={{
                            width: '100%',
                            height: '4px',
                            backgroundColor: 'var(--color-gray-200)',
                            borderRadius: 'var(--radius-full)',
                            marginTop: 'var(--spacing-2)',
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              width: `${uploadProgress[file.name]}%`,
                              height: '100%',
                              backgroundColor: uploadErrors[file.name] ? 'var(--color-error)' : 'var(--color-success)',
                              transition: 'width 0.3s ease',
                            }}
                          />
                        </div>
                      )}
                      {uploadErrors[file.name] && (
                        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-error)', marginTop: 'var(--spacing-1)' }}>
                          Error: {uploadErrors[file.name]}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleRemoveFile(index)}
                      disabled={uploading}
                      style={{
                        padding: 'var(--spacing-2)',
                        background: 'none',
                        border: 'none',
                        color: 'var(--color-gray-400)',
                        cursor: 'pointer',
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Metadata Section */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">Footage Details</h2>
            </div>

            <div className="form-group">
              <label className="form-label">
                <MapPin size={16} style={{ display: 'inline', marginRight: 'var(--spacing-2)' }} />
                Location *
              </label>
              <input
                type="text"
                className="form-input"
                value={metadata.location}
                onChange={(e) => setMetadata({ ...metadata, location: e.target.value })}
                placeholder="e.g., Main Street & 5th Avenue"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <Calendar size={16} style={{ display: 'inline', marginRight: 'var(--spacing-2)' }} />
                Date *
              </label>
              <input
                type="date"
                className="form-input"
                value={metadata.date}
                onChange={(e) => setMetadata({ ...metadata, date: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Time *</label>
              <input
                type="time"
                className="form-input"
                value={metadata.time}
                onChange={(e) => setMetadata({ ...metadata, time: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <User size={16} style={{ display: 'inline', marginRight: 'var(--spacing-2)' }} />
                Therapist
              </label>
              <input
                type="text"
                className="form-input"
                value={user?.name || ''}
                disabled
                style={{ backgroundColor: 'var(--color-gray-100)' }}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Additional Notes</label>
              <textarea
                className="form-textarea"
                rows={4}
                value={metadata.notes}
                onChange={(e) => setMetadata({ ...metadata, notes: e.target.value })}
                placeholder="Enter any additional details about the footage..."
              />
            </div>

            <button
              onClick={handleUpload}
              disabled={uploading || selectedFiles.length === 0}
              className="btn btn-primary"
              style={{ width: '100%' }}
            >
              {uploading ? 'Uploading...' : 'Upload Footage'}
            </button>
          </div>
        </div>

        {/* Info Card */}
        <div className="card" style={{ marginTop: 'var(--spacing-6)', backgroundColor: 'var(--color-gray-50)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-4)' }}>
            <div
              style={{
                padding: 'var(--spacing-3)',
                backgroundColor: 'var(--color-primary)',
                color: 'white',
                borderRadius: 'var(--radius-lg)',
              }}
            >
              <UploadIcon size={24} />
            </div>
            <div>
              <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-2)' }}>
                Upload Guidelines
              </h3>
              <ul style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', lineHeight: '1.8' }}>
                <li>• Supported formats: MP4, AVI, MOV, WMV</li>
                <li>• Maximum file size: 2GB per file</li>
                <li>• Always include accurate location and time information</li>
                <li>• Footage is automatically organized by date and therapist</li>
                <li>• All uploads are encrypted and securely stored in Firebase Storage</li>
                <li>• View uploaded footage in the Footage Library</li>
              </ul>
            </div>
          </div>
        </div>
    </div>
  );
};
